/**
 * PURPOSE: Presentación de las versiones de uniformes de un torneo como espacios de trabajo (FTR-0046) y del
 *   historial de uniformes de un partido (FTR-0044).
 * RESPONSIBILITIES: Títulos y fechas de versión (hora de Lima), número de la próxima versión, textos de
 *   confirmación de nueva versión, cambio de versión y eliminación (dejan explícito qué cambia y qué no),
 *   resultado de un cambio de versión, nombres de lado/clase y valores (opción o texto) y rótulos/colores del origen
 *   de cada cambio del historial. No decide nada: el backend guarda, copia y cambia versiones.
 * COLLABORATORS: UniformVersionsPanel.jsx, ModalMatchUniformHistory.jsx,
 *   uniformOptions.utils.js.
 * ROUTE: winscore/src/utils/uniformVersionsDisplay.js
 */
import { LIMA_TIME_ZONE, contextualOptionLabel } from "../admin/AdminCompany/uniformOptions.utils.js";

const EXCLUSIVE_LABELS = Object.freeze({ GOALKEEPER: "Arquero", LIBERO: "Líbero" });

export const versionTitle = (version) => (version ? `V${version.number} · ${version.name}` : "");

/** Fecha y hora de Lima ("30/09/2026, 10:15"); "—" si no hay fecha válida. */
export const formatLimaDateTimeShort = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("es-PE", { timeZone: LIMA_TIME_ZONE, dateStyle: "short", timeStyle: "short" });
};

export const nextVersionNumber = (versions = []) => Math.max(0, ...versions.map((version) => Number(version.number) || 0)) + 1;

const uniformsWord = (exclusiveStrategy) =>
  exclusiveStrategy === "LIBERO" ? "camisetas y líberos" : exclusiveStrategy === "GOALKEEPER" ? "camisetas y arqueros" : "camisetas";

/** Confirmación de "Nueva versión": copia de la que está en uso, que pasa a ser la nueva en uso. */
export const createVersionConfirmText = ({ active, nextNumber }) =>
  `Se creará V${nextNumber} como copia de ${versionTitle(active)} y pasarás a trabajar en V${nextNumber}. ` +
  `Los partidos no cambian y V${active?.number} queda guardada tal como está. ¿Continuar?`;

/** Confirmación de "Usar esta versión". */
export const activateConfirmText = ({ target, active, exclusiveStrategy }) =>
  `Vas a trabajar en ${versionTitle(target)}. Lo que tienes ahora queda guardado en V${active?.number}. ` +
  `Los partidos por jugarse y no publicados mostrarán los uniformes de V${target?.number} (solo ${uniformsWord(exclusiveStrategy)}); ` +
  "los publicados o ya jugados no cambian. ¿Continuar?";

export const deleteConfirmText = (version) => `Se eliminará ${versionTitle(version)}. Los partidos no cambian. ¿Continuar?`;

export const activationResultText = (result = {}) => {
  if (result.alreadyActive) return `Ya estás trabajando en V${result.activated?.number}.`;
  const head =
    `Ahora trabajas en V${result.activated?.number}: ${result.changedSlots ?? 0} uniforme(s) cambiaron en ${result.changedMatches ?? 0} partido(s). ` +
    `V${result.previous?.number} quedó guardada.`;
  const skipped = result.skipped?.length
    ? ` ${result.skipped.length} partido(s) publicados, jugados o con otros participantes no cambiaron.`
    : "";
  const missing = result.missingOptions ? ` ${result.missingOptions} uniforme(s) ya no existen y quedaron sin asignar.` : "";
  return `${head}${skipped}${missing}`;
};

/** "Camiseta local", "Arquero visitante"… */
export const slotText = ({ side, kind }, exclusiveStrategy) => {
  const kindName = kind === "MAIN" ? "Camiseta" : EXCLUSIVE_LABELS[exclusiveStrategy] || "Alterno";
  return `${kindName} ${Number(side) === 1 ? "local" : "visitante"}`;
};

/** Valor guardado o actual de un lado: la opción (con tipo y tono), el texto libre o "Sin asignar". */
export const slotValueText = (value) => {
  if (value?.option) return contextualOptionLabel(value.option);
  if (value?.text) return `“${value.text}”`;
  return "Sin asignar";
};

export const HISTORY_ORIGIN_TONES = Object.freeze({
  AUTOMATIC: "indigo",
  MANUAL: "amber",
  BULK: "sky",
  VERSION_SWITCH: "violet",
  RESTORE: "emerald",
  LEGACY_EDIT: "slate",
});

/** Explicación visible de un cambio; lo anterior a FTR-0044 sin explicación guardada lo dice, no la inventa. */
export const historyReasonText = (event) => {
  if (event?.reason) return event.reason;
  if (event?.origin === "AUTOMATIC") return "Sin explicación registrada (asignación anterior al historial de decisiones).";
  return null;
};

/** Eventos del más reciente al más antiguo (el backend los entrega en orden cronológico). */
export const newestFirst = (events = []) => [...events].reverse();
